import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2SemiBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check, FileText, FolderOpen, History, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { useIntl, type IntlShape, type MessageDescriptor } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import { ObjectWorkspacePatientDetails } from './ObjectWorkspacePatientDetails';
import { ObjectWorkspaceWorkContent } from './ObjectWorkspaceWorkContent';
import {
  PATIENT_HISTORY,
  PATIENT_SESSIONS,
  type ObjectWorkspaceAnchor,
  type ObjectWorkspaceAnchorSource,
  type ObjectWorkspaceFacet,
  type ObjectWorkspaceObject,
  type ObjectWorkspacePatientDetailsState,
  type ObjectWorkspaceWorkEngagement,
} from './objectWorkspace.data';

interface AnchorListProps {
  heading: MessageDescriptor;
  anchors: readonly ObjectWorkspaceAnchorSource[];
  onSelect?: (anchor: ObjectWorkspaceAnchor) => void;
}

function formatAnchorDate(intl: IntlShape, occurredAt: string): string {
  const date = new Date(occurredAt);
  return `${intl.formatDate(date, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })} · ${intl.formatTime(date, { hour: 'numeric', minute: '2-digit' })}`;
}

function AnchorList({ heading, anchors, onSelect }: AnchorListProps): JSX.Element {
  const intl = useIntl();
  const [selectedId, setSelectedId] = useState<string>();

  return (
    <section className="flex flex-col gap-3">
      <TypographyP2SemiBold className="text-text-primary">
        {intl.formatMessage(heading)}
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-1">
        {anchors.map((anchor) => {
          const selected = selectedId === anchor.id;
          const Icon = anchor.kind === 'session' ? FileText : History;

          return (
            <Button
              key={anchor.id}
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={selected}
              className={cn(
                'h-auto w-full justify-start gap-3 whitespace-normal rounded-lg px-3 py-2.5 text-start',
                selected && 'bg-surface-selected hover:bg-surface-selected-hover'
              )}
              onClick={() => {
                setSelectedId(anchor.id);
                onSelect?.({
                  id: anchor.id,
                  kind: anchor.kind,
                  title: intl.formatMessage(anchor.title),
                  occurredAt: anchor.occurredAt,
                  detail: anchor.detail ? intl.formatMessage(anchor.detail) : undefined,
                });
              }}
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-background-tertiary">
                <Icon className="size-4 text-text-secondary" aria-hidden />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span
                  className={cn(
                    typographyVariant({ variant: 'p2' }),
                    'line-clamp-2 text-text-primary'
                  )}
                >
                  {intl.formatMessage(anchor.title)}
                </span>
                <span
                  className={cn(
                    typographyVariant({ variant: 'caption' }),
                    'text-text-tertiary'
                  )}
                >
                  {formatAnchorDate(intl, anchor.occurredAt)}
                  {anchor.detail ? ` · ${intl.formatMessage(anchor.detail)}` : null}
                </span>
              </span>
              {selected ? (
                <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
              ) : null}
            </Button>
          );
        })}
      </div>
    </section>
  );
}

interface PlaceholderFacetProps {
  heading: MessageDescriptor;
  description: MessageDescriptor;
  documents?: boolean;
}

function PlaceholderFacet({
  heading,
  description,
  documents = false,
}: PlaceholderFacetProps): JSX.Element {
  const intl = useIntl();
  const Icon = documents ? FolderOpen : ListChecks;

  return (
    <section className="rounded-xl border border-border bg-surface-page p-4">
      <Icon className="mb-3 size-5 text-text-secondary" aria-hidden />
      <TypographyP2SemiBold className="text-text-primary">
        {intl.formatMessage(heading)}
      </TypographyP2SemiBold>
      <TypographyP2 className="mt-1 text-text-secondary">
        {intl.formatMessage(description)}
      </TypographyP2>
      {documents ? (
        <TypographyCaption className="mt-3 text-text-tertiary">
          {intl.formatMessage(messages.noDocuments)}
        </TypographyCaption>
      ) : null}
    </section>
  );
}

export function ObjectWorkspaceFacetContent({
  facet,
  kind,
  patientDetailsState,
  onAnchorSelect,
  onWorkEngage,
}: {
  facet: ObjectWorkspaceFacet;
  kind: ObjectWorkspaceObject['kind'];
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onWorkEngage?: (work: ObjectWorkspaceWorkEngagement) => void;
}): JSX.Element {
  if (facet === 'work') {
    return <ObjectWorkspaceWorkContent kind={kind} onWorkEngage={onWorkEngage} />;
  }
  if (facet === 'details' && kind === 'patient') {
    return (
      <ObjectWorkspacePatientDetails
        state={patientDetailsState ?? { status: 'ready', details: {} }}
      />
    );
  }
  if (facet === 'sessions') {
    return (
      <AnchorList
        heading={messages.sessionsHeading}
        anchors={PATIENT_SESSIONS}
        onSelect={onAnchorSelect}
      />
    );
  }
  if (facet === 'history') {
    return (
      <AnchorList
        heading={messages.historyHeading}
        anchors={PATIENT_HISTORY}
        onSelect={onAnchorSelect}
      />
    );
  }
  if (facet === 'context') {
    return (
      <PlaceholderFacet
        heading={messages.contextHeading}
        description={messages.contextDescription}
      />
    );
  }
  if (facet === 'plan') {
    return (
      <PlaceholderFacet
        heading={messages.planHeading}
        description={messages.planDescription}
      />
    );
  }
  return (
    <PlaceholderFacet
      heading={messages.documentsHeading}
      description={messages.documentsDescription}
      documents
    />
  );
}
